import CheckboxFormField from 'components/form/CheckboxFormField';
import TextFormField from 'components/form/TextFormField';
import { useFormik } from 'formik';
import { Box, Button } from 'grommet';
import AssetImageEditor from 'modules/admin/components/AssetImageEditor';
import MarkdownEditor from 'modules/admin/components/MarkdownEditor';
import { actions } from 'modules/asset/asset$';
import { colors } from 'modules/root/theme';
import React, { useEffect } from 'react';
import { dispatchAction } from 'rxbeach';
import { Asset, BaseAsset } from 'types';
import { boolean, object, string } from 'yup';

interface NewsEditorProps {
  asset: Asset | BaseAsset;
  onDelete: () => void;
}

const validationSchema = object({
  label: string().required('Må ha med tittel'),
  markdown: string().required('Må ha med tekst'),
  type: string().required(),
  image: string().nullable(),
  featured: boolean(),
  published: boolean(),
});

const isNew = (test: any): test is BaseAsset[] => !test.id;

const initialValues = {
  label: '',
  type: 'news',
  markdown: '',
  published: false,
  featured: false,
};

const NewsEditor = ({ asset, onDelete }: NewsEditorProps) => {
  const {
    touched,
    values,
    errors,
    handleChange,
    handleBlur,
    setValues,
    submitForm,
    setFieldValue,
  } = useFormik({
    initialValues: { ...initialValues, ...asset },
    validationSchema,
    onSubmit: (data) => {
      if (isNew(data)) dispatchAction(actions.apiCreate([data]));
      else dispatchAction(actions.apiPut([data as Asset]));
    },
  });

  useEffect(() => {
    setValues({ ...initialValues, ...asset });
  }, [JSON.stringify(asset)]);

  const formProps = {
    touched,
    errors,
    values,
    handleChange,
    handleBlur,
    setValues,
    setFieldValue,
  };
  return (
    <>
      <Box direction="row" style={{ minHeight: '40px', height: '40px' }}>
        <Button
          size="small"
          primary
          label={isNew(values) ? 'Opprett' : 'Lagre'}
          disabled={
            Object.keys(errors).length !== 0 &&
            Object.keys(touched).length !== 0
          }
          onClick={() => submitForm()}
        />
        {!isNew(values) && (
          <Button
            color={colors.danger.background}
            label="Slett"
            size="small"
            secondary
            onClick={() => {
              dispatchAction(actions.apiDelete([(asset as Asset).id]));
              onDelete();
            }}
          />
        )}
      </Box>
      <Box pad="medium" height={{ min: 'small' }}>
        <Box justify="start" pad="small" direction="row" gap="small">
          <Box width="medium">
            <TextFormField {...formProps} label="Tittel" id="label" />
            <CheckboxFormField {...formProps} label="Synlig" id="published" />
            <CheckboxFormField {...formProps} label="Fremhev" id="featured" />
          </Box>
          <AssetImageEditor {...formProps} />
        </Box>
      </Box>
      <Box pad="medium">
        <MarkdownEditor
          markdown={values.markdown}
          setMarkdown={(value: string) => setFieldValue('markdown', value)}
        />
      </Box>
    </>
  );
};

export default NewsEditor;
